import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Star, TrendingUp, Award } from 'lucide-react';
import { specializations } from '../mockData';

const Specializations = () => {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState(null);
  
  // Show only first 8 specializations (2 rows of 4)
  const displayedSpecializations = specializations.slice(0, 8);

  // Add popular/trending badges to specific specializations
  const getBadge = (name) => {
    const badges = {
      'General Medicine': { label: 'Most Popular', icon: Star, color: '#FFB800' },
      'Cardiology': { label: 'Top Rated', icon: Award, color: '#FF6B6B' },
      'Pediatrics': { label: 'Trending', icon: TrendingUp, color: '#28C08A' }
    };
    return badges[name] || null;
  };

  // Custom SVG illustrations for each specialty - Enhanced
  const getSpecialtySVG = (name) => {
    const svgs = {
      'General Medicine': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="genMedGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#E8F4FF', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#D6EBFF', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <circle cx="100" cy="100" r="90" fill="url(#genMedGrad)"/>
          <circle cx="100" cy="80" r="35" fill="#FFD28A"/>
          <path d="M65 115 Q65 100 100 100 Q135 100 135 115 L135 160 Q135 170 125 170 L75 170 Q65 170 65 160 Z" fill="white"/>
          <path d="M70 120 L70 145 M130 120 L130 145" stroke="#286CFF" strokeWidth="3"/>
          <circle cx="85" cy="150" r="6" fill="#286CFF"/>
          <circle cx="115" cy="150" r="6" fill="#286CFF"/>
          <path d="M85 150 Q100 155 115 150" stroke="#286CFF" strokeWidth="2" fill="none"/>
          <circle cx="100" cy="115" r="15" fill="#286CFF" opacity="0.1"/>
        </svg>
      ),
      'Cardiology': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="cardioGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#FFE8E8', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#FFD6D6', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <circle cx="100" cy="100" r="90" fill="url(#cardioGrad)"/>
          <path d="M100 140 L70 110 Q60 100 60 85 Q60 70 70 65 Q80 60 90 70 L100 80 L110 70 Q120 60 130 65 Q140 70 140 85 Q140 100 130 110 Z" fill="#FF6B6B"/>
          <path d="M85 95 L95 105 L115 85" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round"/>
          <circle cx="75" cy="85" r="3" fill="white"/>
          <circle cx="125" cy="85" r="3" fill="white"/>
          <path d="M50 115 L60 115 L65 105 L70 125 L75 115 L85 115" stroke="#FF6B6B" strokeWidth="2" fill="none"/>
        </svg>
      ),
      'Dermatology': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="dermaGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#FFF8E8', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#FFF0D6', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <circle cx="100" cy="100" r="90" fill="url(#dermaGrad)"/>
          <circle cx="100" cy="100" r="60" fill="#FFD28A"/>
          <circle cx="80" cy="90" r="8" fill="white" opacity="0.8"/>
          <circle cx="120" cy="95" r="6" fill="white" opacity="0.8"/>
          <circle cx="100" cy="130" r="10" fill="white" opacity="0.8"/>
          <path d="M85 110 Q100 115 115 110" stroke="#4A4A4A" strokeWidth="2" fill="none"/>
          <circle cx="65" cy="75" r="4" fill="#FFA94D" opacity="0.6"/>
          <circle cx="135" cy="120" r="5" fill="#FFA94D" opacity="0.6"/>
        </svg>
      ),
      'Pediatrics': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="pedGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#E8F8FF', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#D6F0FF', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <circle cx="100" cy="100" r="90" fill="url(#pedGrad)"/>
          <circle cx="100" cy="75" r="25" fill="#FFD28A"/>
          <circle cx="90" cy="70" r="3" fill="#4A4A4A"/>
          <circle cx="110" cy="70" r="3" fill="#4A4A4A"/>
          <path d="M95 80 Q100 83 105 80" stroke="#4A4A4A" strokeWidth="2" fill="none"/>
          <rect x="75" y="100" width="50" height="60" rx="5" fill="#79A8FF"/>
          <circle cx="100" cy="130" r="8" fill="white"/>
          <path d="M100 122 L100 138 M92 130 L108 130" stroke="#79A8FF" strokeWidth="3"/>
          <circle cx="80" cy="55" r="8" fill="#FFE4B5" opacity="0.6"/>
          <circle cx="120" cy="55" r="8" fill="#FFE4B5" opacity="0.6"/>
        </svg>
      ),
      'Orthopedics': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="orthoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#F0F8FF', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#E3F0FF', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <circle cx="100" cy="100" r="90" fill="url(#orthoGrad)"/>
          <rect x="85" y="50" width="30" height="100" rx="15" fill="#286CFF" opacity="0.3"/>
          <rect x="90" y="60" width="20" height="80" rx="10" fill="white"/>
          <line x1="100" y1="70" x2="100" y2="130" stroke="#286CFF" strokeWidth="3"/>
          <circle cx="100" cy="75" r="5" fill="#286CFF"/>
          <circle cx="100" cy="95" r="5" fill="#286CFF"/>
          <circle cx="100" cy="115" r="5" fill="#286CFF"/>
          <path d="M75 80 L85 90 M75 100 L85 110" stroke="#5B8BFF" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      'Gynecology': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="gynoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#FFE8F8', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#FFD6F0', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <circle cx="100" cy="100" r="90" fill="url(#gynoGrad)"/>
          <circle cx="100" cy="90" r="40" fill="#FF6B9D" opacity="0.3"/>
          <path d="M100 130 L100 160 M85 145 L115 145" stroke="#FF6B9D" strokeWidth="5"/>
          <circle cx="100" cy="90" r="25" fill="white"/>
          <path d="M85 90 Q85 75 100 75 Q115 75 115 90" fill="#FF6B9D"/>
          <circle cx="100" cy="88" r="6" fill="#FFB6D9"/>
        </svg>
      ),
      'Neurology': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="neuroGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#F5E8FF', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#EBD6FF', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <circle cx="100" cy="100" r="90" fill="url(#neuroGrad)"/>
          <circle cx="100" cy="100" r="50" fill="#9B7EFF" opacity="0.3"/>
          <path d="M70 90 Q80 70 100 70 Q120 70 130 90" stroke="#9B7EFF" strokeWidth="3" fill="none"/>
          <circle cx="85" cy="95" r="8" fill="#9B7EFF"/>
          <circle cx="100" cy="105" r="8" fill="#9B7EFF"/>
          <circle cx="115" cy="95" r="8" fill="#9B7EFF"/>
          <path d="M85 95 L100 105 L115 95" stroke="#9B7EFF" strokeWidth="2"/>
          <circle cx="70" cy="110" r="5" fill="#C4A8FF" opacity="0.6"/>
          <circle cx="130" cy="110" r="5" fill="#C4A8FF" opacity="0.6"/>
        </svg>
      ),
      'Ophthalmology': (
        <svg viewBox="0 0 200 200" style={{ width: '100%', height: '100%' }}>
          <defs>
            <linearGradient id="ophthaGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#E8F5FF', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#D6EBFF', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <circle cx="100" cy="100" r="90" fill="url(#ophthaGrad)"/>
          <ellipse cx="100" cy="100" rx="50" ry="30" fill="white" stroke="#286CFF" strokeWidth="3"/>
          <circle cx="100" cy="100" r="20" fill="#286CFF"/>
          <circle cx="100" cy="100" r="12" fill="#4A4A4A"/>
          <circle cx="105" cy="95" r="5" fill="white"/>
          <path d="M60 80 Q70 70 85 75 M140 80 Q130 70 115 75" stroke="#286CFF" strokeWidth="2" fill="none"/>
        </svg>
      )
    };
    return svgs[name] || svgs['General Medicine'];
  };

  return (
    <section id="specializations" style={styles.section}>
      {/* Background Elements */}
      <div style={styles.backgroundPattern}>
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.floatingShape,
              left: `${15 + i * 15}%`,
              top: `${10 + (i % 2) * 40}%`,
              width: `${60 + i * 20}px`,
              height: `${60 + i * 20}px`,
              animationDelay: `${i * 1}s`
            }}
          />
        ))}
      </div>

      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.sectionBadge}>
            <Award size={18} style={{ color: '#286CFF' }} />
            <span>Expert Care</span>
          </div>
          <h2 style={styles.title}>
            Our Medical <span style={styles.titleGradient}>Specializations</span>
          </h2>
          <p style={styles.subtitle}>
            Comprehensive healthcare services across all major medical specialties
            with board-certified doctors
          </p>
        </div>

        <div style={styles.grid}>
          {displayedSpecializations.map((spec, index) => {
            const badge = getBadge(spec.name);
            const BadgeIcon = badge?.icon;
            
            return (
              <div
                key={spec.id}
                style={{
                  ...styles.card,
                  animationDelay: `${index * 0.1}s`,
                  transform: hoveredCard === spec.id ? 'translateY(-12px) scale(1.02)' : 'translateY(0) scale(1)',
                  boxShadow: hoveredCard === spec.id 
                    ? '0 20px 60px rgba(40, 108, 255, 0.2), 0 0 0 3px rgba(40, 108, 255, 0.1)'
                    : '0 8px 24px rgba(0, 0, 0, 0.08)'
                }}
                className="animate-fade-in-up"
                onClick={() => navigate('/specializations')}
                onMouseEnter={() => setHoveredCard(spec.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* Badge */}
                {badge && (
                  <div style={{ ...styles.badge, background: `${badge.color}15`, borderColor: badge.color }}>
                    {BadgeIcon && <BadgeIcon size={12} style={{ color: badge.color }} />}
                    <span style={{ color: badge.color }}>{badge.label}</span>
                  </div>
                )}

                {/* SVG Icon */}
                <div style={{
                  ...styles.svgWrapper,
                  transform: hoveredCard === spec.id ? 'scale(1.1) rotate(-3deg)' : 'scale(1) rotate(0deg)'
                }}>
                  {getSpecialtySVG(spec.name)}
                </div>

                {/* Title */}
                <h3 style={styles.cardTitle}>{spec.name}</h3>
                
                {/* Description */}
                <p style={styles.cardDescription}>{spec.description}</p>

                {/* Arrow Icon */}
                <div style={{
                  ...styles.arrowIcon,
                  opacity: hoveredCard === spec.id ? 1 : 0,
                  transform: hoveredCard === spec.id ? 'translateX(0)' : 'translateX(-10px)'
                }}>
                  <ArrowRight size={20} style={{ color: '#286CFF' }} />
                </div>
              </div>
            );
          })}
        </div>

        <div style={styles.viewAllWrapper}>
          <button 
            style={styles.viewAllBtn}
            onClick={() => navigate('/specializations')}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #286CFF 0%, #5B8BFF 100%)';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(40, 108, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.color = '#286CFF';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <span>See All Specializations</span>
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: {
    padding: '120px 0',
    background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFF 50%, #FFFFFF 100%)',
    position: 'relative',
    overflow: 'hidden'
  },
  backgroundPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    opacity: 0.4
  },
  floatingShape: {
    position: 'absolute',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(40, 108, 255, 0.08), rgba(255, 184, 0, 0.08))',
    animation: 'float 20s infinite ease-in-out'
  },
  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 32px',
    position: 'relative',
    zIndex: 1
  },
  header: {
    textAlign: 'center',
    marginBottom: '72px'
  },
  sectionBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)',
    border: '2px solid #286CFF',
    borderRadius: '24px',
    padding: '10px 24px',
    marginBottom: '24px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#286CFF',
    boxShadow: '0 4px 12px rgba(40, 108, 255, 0.15)'
  },
  title: {
    fontSize: '56px',
    fontWeight: '900',
    lineHeight: '1.1',
    color: '#1A202C',
    marginBottom: '24px',
    letterSpacing: '-1px'
  },
  titleGradient: {
    background: 'linear-gradient(135deg, #286CFF 0%, #5B8BFF 50%, #FFB800 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  subtitle: {
    fontSize: '20px',
    color: '#64748B',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.7'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '28px'
  },
  card: {
    background: 'white',
    border: '2px solid transparent',
    backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #E8ECF0, #F0F4FF)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
    borderRadius: '24px',
    padding: '36px 28px',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    textAlign: 'center',
    position: 'relative',
    opacity: 0,
    animation: 'fadeInUp 0.6s ease-out forwards'
  },
  badge: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    border: '1.5px solid',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
  },
  svgWrapper: {
    width: '120px',
    height: '120px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.08))'
  },
  cardTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1A202C',
    marginBottom: '12px',
    lineHeight: '1.3'
  },
  cardDescription: {
    fontSize: '15px',
    color: '#64748B',
    lineHeight: '1.7',
    minHeight: '48px'
  },
  arrowIcon: {
    position: 'absolute',
    bottom: '24px',
    right: '24px',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #E3EDFF, #F0F6FF)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(40, 108, 255, 0.2)'
  },
  viewAllWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '64px'
  },
  viewAllBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: 'white',
    color: '#286CFF',
    border: '3px solid #286CFF',
    borderRadius: '50px',
    padding: '18px 48px',
    fontSize: '18px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: 'none',
    letterSpacing: '0.3px'
  }
};

export default Specializations;
